import styled from "styled-components";
import {
  faEye,
  faPlus,
  faImage,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ImagePreviewModal from "../funnel-form/preview-image-modal";
import { Form, OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import { MODAL_TYPE } from "../strings";
import { useDialog } from "../../hooks/useDialog";
import { showError } from "../../services/toast-service";
import { ContainObjFitImg, Legend } from "../common-styling";
import CreateFavIconModal from "../gallery/create-favicon-modal";
import { deleteFavIconAsset } from "../../services/api-declaration";
import ValidationMessage from "../validation-message/validation-message";
import { useTranslation } from "react-i18next";

const IconContainer = styled.div`
  flex: 1;
  margin-right: 15px;
  padding-left: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #c0c4c5;
`;
const FAIcon = styled(FontAwesomeIcon)`
  color: #616568;
  font-size: 40px;
`;
const FixedWidthImgDiv = styled.div`
  width: 68px;
  height: 54px;
`;

const FavIconInput = ({ assetData, assetKey, ...props }) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [showCreateGalleryModal, setShowCreateGalleryModal] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const { setValue, watch, register } = useFormContext();
  const logoDisplay = watch("design.partnerLogoDisplay");
  const { showConfirmationDialogBox } = useDialog();
  const { REACT_APP_ASSET_CDN_URL } = process.env;

  const toggleModal = (modal) => {
    if (modal === MODAL_TYPE.CREATE) {
      setShowCreateGalleryModal(!showCreateGalleryModal);
    } else {
      setShowCreateGalleryModal(false);
    }
  };

  useEffect(() => {
    if (assetKey) {
      setImgSrc(`${REACT_APP_ASSET_CDN_URL}/${assetKey}`);
    }
  }, [assetKey, REACT_APP_ASSET_CDN_URL]);

  const selectImage = ({ url, key, _id, isNew, isUpdated }) => {
    try {
      if (isNew) {
        const fieldObj = { key, url, _id, asset: _id, isUpdated };
        setImgSrc(`${REACT_APP_ASSET_CDN_URL}/${key}`);
        setValue(props.name, fieldObj, { shouldDirty: true });
      } else {
        const newImgSrc =
          `${REACT_APP_ASSET_CDN_URL}/${assetKey}?v=${Date.now()}`.replace(
            "release",
            "draft"
          );
        setImgSrc(newImgSrc);
        setValue(
          props.name,
          {
            ...assetData,
            key: assetKey.replace("release", "draft"),
            isUpdated
          },
          { shouldDirty: true }
        );
      }
      toggleModal();
      if (props.isFormChanged) {
        props.isFormChanged.current = true;
      }
      if (props.setIsFormChanged) {
        props.setIsFormChanged(true);
      }
    } catch (error) {
      showError(
        error?.response?.data?.message || t("toastMessage.selectAssetError")
      );
    }
  };
  const renderDeleteDialogMessage = () => (
    <div
      dangerouslySetInnerHTML={{
        __html: t("customerWebsite.deleteFaviconMessage")
      }}
    ></div>
  );

  const removeImage = async (name) => {
    const fav = watch(name);

    showConfirmationDialogBox({
      dialogMessage: renderDeleteDialogMessage(),
      dialogType: "danger",
      title: t("customerWebsite.deleteFavicon"),
      responseAction: async (action) => {
        if (action) {
          try {
            await deleteFavIconAsset(fav?._id, { key: fav?.key });

            setValue(name, null, {
              shouldValidate: true,
              shouldDirty: true
            });

            setImgSrc(null);

            if (props.isFormChanged) {
              props.isFormChanged.current = true;
            }
            if (props.setIsFormChanged) {
              props.setIsFormChanged(true);
            }
          } catch (error) {
            showError(
              error?.response?.data?.message ||
                t("toastMessage.deleteAssetError")
            );
          }
        }
      }
    });
  };

  return (
    <div className="mb-4">
      <div className="d-flex">
        <Legend>
          <h6
            className={`mb-2 fw-bold ${
              props.error?.message ? "text-danger fw-bold" : ""
            }`}
          >
            {props.label}
            {props.suggestionText && (
              <small className="mx-2 fw-normal text-muted">
                {props.suggestionText}
              </small>
            )}
          </h6>
        </Legend>
        {props.setDisplay && (
          <div className="ms-2">
            <OverlayTrigger
              overlay={
                <Tooltip>{logoDisplay ? "Display On" : "Display Off"}</Tooltip>
              }
            >
              <Form.Check
                role="button"
                type="switch"
                {...register("design.partnerLogoDisplay")}
              />
            </OverlayTrigger>
          </div>
        )}
      </div>
      <div className="d-flex align-items-center">
        <IconContainer>
          {assetData?.url ? (
            <FixedWidthImgDiv>
              <ContainObjFitImg src={imgSrc} />
            </FixedWidthImgDiv>
          ) : (
            <FAIcon icon={faImage} />
          )}
        </IconContainer>
        {assetData?.url && (
          <>
            <Button
              variant="outline-secondary me-2"
              onClick={() => setShowPreview(!showPreview)}
            >
              <FontAwesomeIcon icon={faEye} />
            </Button>
            {showPreview && (
              <ImagePreviewModal
                source={imgSrc}
                toggleModal={() => setShowPreview(!showPreview)}
              />
            )}
          </>
        )}
        {props.isUploadAllowed && (
          <Button
            onClick={() => toggleModal(MODAL_TYPE.CREATE)}
            variant="outline-secondary"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {t("suggestionGallery.upload")}
          </Button>
        )}
        {props.isRemoveAllowed && (
          <Button
            onClick={() => removeImage(props.name)}
            variant="outline-danger mx-2"
            disabled={props.removeBtnDisabled}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        )}

        {showCreateGalleryModal && (
          <CreateFavIconModal
            onFinish={selectImage}
            onClose={() => toggleModal(MODAL_TYPE.CREATE)}
            parentFolderName={props.parentFolderName}
            resourceId={props.resourceId}
            assetId={assetData?._id}
          />
        )}
      </div>
      {props.error?.message && (
        <ValidationMessage error={props.error.message} />
      )}
    </div>
  );
};
export default FavIconInput;
